import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Fragment, useState, type ReactElement, type ReactNode } from 'react';

export type FilterOption = {
    label: string;
    value: string;
};

type FilterSectionBase = {
    // Stable identifier so callers can key into selection state.
    id: string;
    // Subheader label rendered above the section.
    title: string;
};
export type FilterSection =
    | (FilterSectionBase & { options: FilterOption[]; content?: never })
    | (FilterSectionBase & { content: ReactNode; options?: never });

export type FiltersProps = {
    sections: FilterSection[];
    // Selected values per section, keyed by `FilterSection.id`.
    selected?: Record<string, string[]>;
    onSelectionChange?: (sectionId: string, values: string[]) => void;
    search?: string;
    onSearchChange?: (search: string) => void;
    searchPlaceholder: string;
    // Render a divider between consecutive sections.
    dividers?: boolean;
    // Let each section collapse via a chevron toggle in its header.
    collapsible?: boolean;
    // Accessible name for the panel landmark; the surrounding surface owns the visible heading.
    ariaLabel: string;
};

function toggleValue(values: readonly string[], value: string): string[] {
    return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

export function Filters({
    sections,
    selected = {},
    onSelectionChange,
    search = '',
    onSearchChange,
    searchPlaceholder,
    dividers = false,
    collapsible = false,
    ariaLabel,
}: FiltersProps): ReactElement {
    const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(new Set());

    const toggleSection = (id: string): void =>
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });

    return (
        <Stack component="section" spacing={1} aria-label={ariaLabel}>
            <TextField
                placeholder={searchPlaceholder}
                size="small"
                fullWidth
                value={search}
                onChange={(e) => onSearchChange?.(e.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <List component="div" disablePadding>
                {sections.map((section, index) => {
                    const selectedValues = selected[section.id] ?? [];
                    const expanded = !collapsedIds.has(section.id);
                    const bodyId = `filter-section-${section.id}`;
                    const body =
                        section.content ??
                        section.options?.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                control={
                                    <Checkbox
                                        size="small"
                                        color="secondary"
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() =>
                                            onSelectionChange?.(section.id, toggleValue(selectedValues, option.value))
                                        }
                                    />
                                }
                                label={option.label}
                                sx={{ display: 'flex', ml: 0 }}
                            />
                        ));

                    return (
                        <Fragment key={section.id}>
                            {dividers && index > 0 && <Divider />}
                            <Box sx={{ pb: collapsible ? 1 : 0 }}>
                                <ListSubheader
                                    disableSticky
                                    disableGutters
                                    component={collapsible ? 'div' : 'li'}
                                    sx={{ px: 2, bgcolor: 'transparent' }}>
                                    {collapsible ? (
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={() => toggleSection(section.id)}
                                            aria-expanded={expanded}
                                            aria-controls={bodyId}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                border: 0,
                                                background: 'none',
                                                p: 0,
                                                font: 'inherit',
                                                color: 'inherit',
                                                cursor: 'pointer',
                                            }}>
                                            {section.title}
                                            <Box
                                                component="span"
                                                aria-hidden
                                                sx={{ display: 'flex', color: 'action.active' }}>
                                                {expanded ? (
                                                    <ExpandLessIcon fontSize="small" />
                                                ) : (
                                                    <ExpandMoreIcon fontSize="small" />
                                                )}
                                            </Box>
                                        </Box>
                                    ) : (
                                        section.title
                                    )}
                                </ListSubheader>
                                {collapsible ? (
                                    <Collapse in={expanded} unmountOnExit>
                                        <Box id={bodyId}>{body}</Box>
                                    </Collapse>
                                ) : (
                                    body
                                )}
                            </Box>
                        </Fragment>
                    );
                })}
            </List>
        </Stack>
    );
}
